<template>
  <button
    :class="['header-close-button', { '-is-open': isOpen }]"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave">
    <client-only>
      <vue3-lottie
        ref="lottieRef"
        :animation-data="menuAnimation"
        :auto-play="false"
        :loop="false"
        :speed="1.5"
        width="24"
        height="24"
        class="header-close-button__animation"
        @on-animation-loaded="onAnimationLoaded" />
    </client-only>
    <span
      v-if="isOpen"
      class="header-close-button__text">{{ t("general.close") }}</span>
  </button>
</template>

<script setup>
  import { useWindowSize } from '@vueuse/core'
  import {
    computed, ref, watch
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Vue3Lottie } from 'vue3-lottie'

  import {
    useRoute
  } from '#app'
  import menuAnimation from '~/public/animations/menu-button.json'
  import { useLayoutStore } from '~/store/layout'

  const route = useRoute()
  const { t } = useI18n()
  const { width } = useWindowSize()
  const layout = useLayoutStore()

  const props = defineProps({
    isOpen: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits([ 'close' ])

  const lottieRef = ref()
  const isToggled = ref(false)
  const isAnimationPlaying = ref(false)

  const isMobile = computed(() => width.value < 768)

  // watch(() => route.name, async newPage => {
  //   if(isToggled.value) {
  //     handleClick()
  //   }
  // })
  // Watch for external changes to menu state (e.g., when clicking a menu link)
  watch(() => props.isOpen, newValue => {
    if (!lottieRef.value) {
      isToggled.value = newValue
      return
    }

    if (!newValue && isToggled.value) {
      // Menu was closed externally, sync the button state
      isToggled.value = false
      if (!isAnimationPlaying.value) {
        isAnimationPlaying.value = true
        lottieRef.value.playSegments([ 60, 0 ], true)
        setTimeout(() => {
          isAnimationPlaying.value = false
        }, 600)
      }
    } else if (newValue && !isToggled.value) {
      // Menu was opened externally, sync the button state and animate open
      isToggled.value = true
      if (!isAnimationPlaying.value) {
        isAnimationPlaying.value = true
        lottieRef.value.playSegments([ 60, 120 ], true)
        setTimeout(() => {
          isAnimationPlaying.value = false
        }, 600)
      }
    }
  })

  const onAnimationLoaded = () => {
    if (!lottieRef.value) return
  }

  const play = direction => {
    if (!lottieRef.value) return
    lottieRef.value.setDirection(direction)
    lottieRef.value.play()
  }

  const playReverse = () => play('reverse')

  const handleMouseEnter = () => {
    if (isMobile.value || isAnimationPlaying.value) return

    if (isToggled.value) {
      // closing
      lottieRef.value.playSegments([ 120, 180 ], true)
    } else {
      // opening
      lottieRef.value.playSegments([ 0, 45 ], true)
    }
  }

  const handleMouseLeave = () => {
    if (isMobile.value || isAnimationPlaying.value) return
    playReverse()
  }

  const handleClick = () => {
    isAnimationPlaying.value = true
    if (isToggled.value) {
      // closing
      lottieRef.value.playSegments([ 60, 0 ], true)
      emit('close')
    } else {
      // opening
      lottieRef.value.playSegments([ 60, 120 ], true)
    }

    setTimeout(() => {
      isAnimationPlaying.value = false
    }, 600)

    isToggled.value = !isToggled.value
  }
</script>

<style lang="scss" scoped>
.header-close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  transition: background-color 0.2s ease;
  width: 24px;
  height: 24px;

  &.-is-open {
    width: auto;
  }

  &:hover,
  &.active {
    background: rgb(220, 8, 20, 0.1);
  }

  &__animation {
    display: flex;
    align-items: center;
  }

  &__text {
    font-size: 12px;
    text-transform: uppercase;
    padding: 0 4px 0 0;
  }
}
</style>
